<template>
  <div class="content-panel" style="max-width: 1000px;">
    <div class="panel-header">
      <div class="panel-icon celebration">
        <v-icon size="32" color="#00C73C">mdi-party-popper</v-icon>
      </div>
      <div class="panel-title-section">
        <h2 class="panel-title">임금관리결과 확인하기</h2>
        <p class="panel-subtitle">모든 준비가 완료되었습니다. 계산된 임금관리결과를 확인하고 출력하세요</p>
      </div>
    </div>

    <div class="panel-body">
      <div class="celebration-box">
        <v-icon color="#00C73C" size="48">mdi-check-circle</v-icon>
        <h3>축하합니다!</h3>
        <p>임금 관리를 위한 모든 단계를 완료했어요</p>
      </div>

      <div class="features-grid">
        <div class="feature-item clickable" @click="handleOpenWageSlip" :class="{ 'disabled': isLoadingData }">
          <v-icon color="#1e3a8a">mdi-account-cash</v-icon>
          <h4>급여 명세서</h4>
          <p>개인별 급여 명세서 확인</p>
        </div>
        <div class="feature-item clickable" @click="handleDownloadExcel" :class="{ 'disabled': isLoadingData }">
          <v-icon color="#1e3a8a">mdi-file-excel</v-icon>
          <h4>임금대장 엑셀 다운로드</h4>
          <p>급여 데이터 엑셀 저장</p>
        </div>
        <div class="feature-item clickable" @click="handleGoDashboard">
          <v-icon color="#1e3a8a">mdi-chart-line</v-icon>
          <h4>기관 운영 현황 보기</h4>
          <p>운영 현황 이동</p>
        </div>
        <div class="feature-item clickable" @click="handleOpenTaxReq" :class="{ 'has-request': hasActiveRequest }">
          <v-icon :color="hasActiveRequest ? '#10b981' : '#1e3a8a'">
            {{ hasActiveRequest ? 'mdi-check-decagram' : 'mdi-file-document-edit' }}
          </v-icon>
          <h4>임금신고 요청</h4>
          <p>{{ taxRequestStatusText }}</p>
          <span v-if="hasActiveRequest" class="request-badge">요청중</span>
        </div>
        <div class="feature-item clickable" @click="handleOpenJobSummary" :class="{ 'disabled': isLoadingData }">
          <v-icon color="#1e3a8a">mdi-chart-bar</v-icon>
          <h4>인건비 지출비율 참고</h4>
          <p>종사자구분별 인원·임금·보험·퇴직금</p>
        </div>
      </div>

      <div v-if="stepCompleted" class="status-card" :class="isConfirmed ? 'info' : 'success'">
        <v-icon :color="isConfirmed ? '#10b981' : '#00C73C'" size="24">
          {{ isConfirmed ? 'mdi-lock-check' : 'mdi-check-circle' }}
        </v-icon>
        <div class="status-card-text">
          <h4>{{ isConfirmed ? '임금관리결과 확정됨' : '임금관리결과 확인 완료' }}</h4>
          <p>
            {{ isConfirmed
              ? '확정 상태예요. 해제 후에만 임금관리결과를 수정할 수 있습니다.'
              : '임금관리결과 확인이 완료되었습니다. 확정을 눌러 완료하세요.' }}
          </p>
        </div>
        <div class="status-card-actions">
          <template v-if="isConfirmed">
            <button 
              class="btn-outline" 
              @click="handleRelease" 
              :disabled="isConfirming || !canReleaseConfirmation"
              :title="!canReleaseConfirmation ? '임금신고 요청 상태에서는 해제할 수 없습니다' : ''"
            >
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              <v-icon v-if="!canReleaseConfirmation" size="16" class="mr-1">mdi-lock</v-icon>
              확정 해제
            </button>
          </template>
          <template v-else>
            <button class="btn-outline" @click="handleConfirm" :disabled="!stepCompleted || isConfirming">
              <v-progress-circular
                v-if="isConfirming"
                indeterminate
                size="16"
                width="2"
                color="#047857"
                class="mr-1"
              />
              임금관리결과 확정하기
            </button>
          </template>
        </div>
      </div>

      <button v-else class="btn-primary-large celebrate" @click="emit('view-result')" :disabled="isConfirmed">
        <v-icon class="btn-icon">mdi-rocket-launch</v-icon>
        <span>임금관리결과 보러 가기</span>
      </button>

      <!-- 엑셀 다운로드 컴포넌트 (숨김) -->
      <WageReportExcel 
        ref="wageReportExcelRef" 
        :leb-ym="yearMonth"
        :work-info-data="workInfoData"
        :sisul-code="store.state.user?.m_sisul_code || ''"
        @show-snackbar="handleSnackbar" 
      />

      <!-- 급여명세서 PDF 컴포넌트 -->
      <WageSlipPdf
        ref="wageSlipPdfRef"
        :grouped-member-data="groupedMemberData"
        :leb-ym="yearMonth"
        :tax-data="taxData"
        :settings-data="settingsData"
        :payroll-details="payrollDetails"
        :work-time-info="workTimeInfo"
        :sabok-staff-data="sabokStaffData"
        :sabok-staff-settings="sabokStaffSettings"
        :payment-date="props.paymentDate"
        @show-snackbar="handleSnackbar"
      />

      <!-- 확정 확인 다이얼로그 -->
      <v-dialog v-model="showConfirmDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header">
            <v-icon size="48" color="#10b981">mdi-lock-check</v-icon>
            <h3>임금관리결과 확정</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>임금관리결과를 확정하시겠습니까?</p>
            <p>확정 후에는 임금관리결과를 수정할 수 없습니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showConfirmDialog = false">취소</button>
            <button class="btn-confirm" @click="proceedConfirm">확정</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 해제 확인 다이얼로그 -->
      <v-dialog v-model="showReleaseDialog" max-width="440px">
        <v-card class="confirm-dialog">
          <div class="confirm-dialog-header warning">
            <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
            <h3>확정 해제</h3>
          </div>
          <div class="confirm-dialog-body">
            <p>임금관리결과 확정을 해제하시겠습니까?</p>
            <p>해제 후에는 다시 수정이 가능합니다.</p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-cancel" @click="showReleaseDialog = false">취소</button>
            <button class="btn-confirm warning" @click="proceedRelease">해제</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- 임금신고 요청 모달 -->
      <WageTaxReq
        v-model="showTaxReqDialog"
        :year-month="yearMonth"
        @request-submitted="handleTaxReqSubmitted"
        @request-cancelled="handleTaxReqCancelled"
      />

      <!-- 인건비 지출비율 참고 모달 -->
      <v-dialog v-model="showJobSummaryDialog" max-width="860px">
        <v-card class="job-summary-dialog">
          <div class="job-summary-header">
            <div class="header-left">
              <v-icon size="28" color="#1e3a8a">mdi-chart-bar</v-icon>
              <div>
                <h3>인건비 지출비율 참고</h3>
                <p class="header-subtitle">{{ formattedYearMonth }} 기준</p>
              </div>
            </div>
            <v-btn icon variant="text" size="small" @click="showJobSummaryDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="job-summary-body">
            <div v-if="jobSummaryData.length === 0" class="empty-state">
              <v-icon size="48" color="#ccc">mdi-database-off</v-icon>
              <p>데이터가 없습니다. 먼저 급여 데이터를 확인해주세요.</p>
            </div>
            <div v-else class="table-wrapper">
              <table class="job-summary-table">
                <thead>
                  <tr>
                    <th class="col-job">종사자구분</th>
                    <th class="col-num">인원</th>
                    <th class="col-money">임금총액</th>
                    <th class="col-money">
                      사회보험<br/><span class="sub-label">(기관부담)</span>
                    </th>
                    <th class="col-money">퇴직적립금</th>
                    <th class="col-money">
                      기관부담 합계<br/><span class="sub-label">(보험+퇴직)</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in jobSummaryData" :key="idx">
                    <td class="col-job">
                      <span class="job-badge" :style="{ background: getJobColor(row.job) }">{{ row.job }}</span>
                    </td>
                    <td class="col-num">{{ row.count }}명</td>
                    <td class="col-money">{{ formatMoney(row.totalPay) }}</td>
                    <td class="col-money">{{ formatMoney(row.socialInsurance) }}</td>
                    <td class="col-money">{{ formatMoney(row.retirement) }}</td>
                    <td class="col-money total-highlight">{{ formatMoney(row.socialInsurance + row.retirement) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td class="col-job"><strong>합계</strong></td>
                    <td class="col-num"><strong>{{ jobSummaryTotals.count }}명</strong></td>
                    <td class="col-money"><strong>{{ formatMoney(jobSummaryTotals.totalPay) }}</strong></td>
                    <td class="col-money"><strong>{{ formatMoney(jobSummaryTotals.socialInsurance) }}</strong></td>
                    <td class="col-money"><strong>{{ formatMoney(jobSummaryTotals.retirement) }}</strong></td>
                    <td class="col-money total-highlight"><strong>{{ formatMoney(jobSummaryTotals.socialInsurance + jobSummaryTotals.retirement) }}</strong></td>
                  </tr>
                </tfoot>
              </table>

              <!-- 사회보험 상세 내역 -->
              <div class="insurance-detail-section">
                <h4 class="detail-title" @click="showInsuranceDetail = !showInsuranceDetail" style="cursor: pointer;">
                  <v-icon size="18">{{ showInsuranceDetail ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  사회보험 기관부담금 상세
                </h4>
                <div v-if="showInsuranceDetail" class="detail-table-wrapper">
                  <table class="job-summary-table detail-table">
                    <thead>
                      <tr>
                        <th class="col-job">종사자구분</th>
                        <th class="col-money-sm">국민연금</th>
                        <th class="col-money-sm">건강보험</th>
                        <th class="col-money-sm">장기요양</th>
                        <th class="col-money-sm">고용보험</th>
                        <th class="col-money-sm">실업급여</th>
                        <th class="col-money-sm">산재보험</th>
                        <th class="col-money-sm">합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in jobSummaryData" :key="'d-'+idx">
                        <td class="col-job">
                          <span class="job-badge" :style="{ background: getJobColor(row.job) }">{{ row.job }}</span>
                        </td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.nationalPension) }}</td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.healthInsurance) }}</td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.longTermCare) }}</td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.employmentInsurance) }}</td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.unemployment) }}</td>
                        <td class="col-money-sm">{{ formatMoney(row.detail.industrialAccident) }}</td>
                        <td class="col-money-sm total-highlight">{{ formatMoney(row.socialInsurance) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="total-row">
                        <td class="col-job"><strong>합계</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.nationalPension) }}</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.healthInsurance) }}</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.longTermCare) }}</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.employmentInsurance) }}</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.unemployment) }}</strong></td>
                        <td class="col-money-sm"><strong>{{ formatMoney(jobSummaryTotals.detail.industrialAccident) }}</strong></td>
                        <td class="col-money-sm total-highlight"><strong>{{ formatMoney(jobSummaryTotals.socialInsurance) }}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="job-summary-footer">
            <v-btn variant="outlined" @click="showJobSummaryDialog = false">닫기</v-btn>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import WageReportExcel from '@/components/wage/WageReportExcel.vue';
import WageSlipPdf from '@/components/wage/WageSlipPdf.vue';
import WageTaxReq from '@/components/wage/tax/WageTaxReq.vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  stepCompleted?: boolean;
  isConfirmed?: boolean;
  isConfirming?: boolean;
  yearMonth?: string;
  paymentDate?: string;
  sanRate?: number | null;
}>();

const emit = defineEmits<{
  (e: 'view-result'): void;
  (e: 'confirm'): void;
  (e: 'release'): void;
  (e: 'require-payment-date'): void;
}>();

const store = useStore();
const router = useRouter();

const isConfirmed = computed(() => !!props.isConfirmed);
const isConfirming = computed(() => !!props.isConfirming);
const stepCompleted = computed(() => props.stepCompleted ?? false);
const yearMonth = computed(() => props.yearMonth || store.state.lebYm || '');

// 확정/해제 확인 다이얼로그 상태
const showConfirmDialog = ref(false);
const showReleaseDialog = ref(false);

// 임금신고 요청 관련
const showTaxReqDialog = ref(false);
const taxRequestData = ref<any>(null);

const hasActiveRequest = computed(() => {
  return taxRequestData.value && taxRequestData.value.wtr_status === '요청';
});

// 확정 해제 가능 여부 (요청하지 않았거나 취소한 경우에만 가능)
const canReleaseConfirmation = computed(() => {
  // 요청 데이터가 없거나 취소 상태면 해제 가능
  if (!taxRequestData.value) return true;
  if (taxRequestData.value.wtr_status === '취소') return true;
  return false;
});

const taxRequestStatusText = computed(() => {
  if (!taxRequestData.value) return '관리자에게 신고 요청';
  switch (taxRequestData.value.wtr_status) {
    case '요청': return '요청 대기중';
    case '승인': return '승인 완료';
    case '반려': return '반려됨';
    case '취소': return '관리자에게 신고 요청';
    default: return '관리자에게 신고 요청';
  }
});

// 인건비 지출비율 참고 모달
const showJobSummaryDialog = ref(false);
const showInsuranceDetail = ref(false);

// 엑셀 다운로드 관련
const wageReportExcelRef = ref<any>(null);
// 급여명세서 PDF 관련
const wageSlipPdfRef = ref<any>(null);
const isLoadingData = ref(false);
const groupedMemberData = ref<any[]>([]);
const taxData = ref<any>(null);
const settingsData = ref<any>({});
const payrollDetails = ref<Record<number, any>>({});      // 급여 상세 (StepThree의 calculatePayrollDetails 결과)
const workTimeInfo = ref<Record<number, any>>({});        // 근무시간 정보
const sabokStaffData = ref<any[]>([]);
const sabokStaffSettings = ref<any>({});
const workInfoData = ref<any[]>([]);  // hh_emp_sabok_work_info 테이블 데이터 (엑셀용)

const hasDownloadableData = computed(() => {
  return workInfoData.value.length > 0;
});

const canDownload = computed(() => {
  return hasDownloadableData.value && !isLoadingData.value;
});

const handleGoDashboard = () => {
  router.push('/pgm/dashboard');
};

// 임금신고 요청 모달 열기
const handleOpenTaxReq = () => {
  showTaxReqDialog.value = true;
};

// 임금신고 요청 완료 시
const handleTaxReqSubmitted = () => {
  loadTaxRequestStatus();
};

// 임금신고 요청 취소 시
const handleTaxReqCancelled = () => {
  loadTaxRequestStatus();
};

// 임금신고 요청 상태 조회
const loadTaxRequestStatus = async () => {
  const user = store.state.user;
  if (!user?.m_sisul_code || !yearMonth.value) return;

  try {
    const response = await axios.get('/api/wage-process/tax-request', {
      params: {
        sisulCode: user.m_sisul_code,
        yearMonth: yearMonth.value
      }
    });

    if (response.data.success) {
      taxRequestData.value = response.data.data;
    }
  } catch (error) {
    console.error('임금신고 요청 상태 조회 오류:', error);
  }
};

// 급여명세서 열기 (모든 직원 임금명세서 한번에 보기)
const handleOpenWageSlip = async () => {
  if (isLoadingData.value) return;
  
  // 임금지급일 확인
  if (!props.paymentDate) {
    store.commit('showSnackbar', {
      text: '임금지급일을 먼저 설정해주세요.',
      color: 'warning'
    });
    emit('require-payment-date');
    return;
  }
  
  if (!hasDownloadableData.value) {
    await loadDownloadData();
  }

  if (!hasDownloadableData.value) {
    store.commit('showSnackbar', {
      text: '급여 데이터가 없습니다. 먼저 급여 데이터를 확인해주세요.',
      color: 'warning'
    });
    return;
  }

  if (wageSlipPdfRef.value) {
    wageSlipPdfRef.value.previewAllWageSlips();
  }
};

// 금액 포맷
const formatMoney = (value: number) => {
  if (!value && value !== 0) return '0';
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
};

// 년월 포맷
const formattedYearMonth = computed(() => {
  const ym = yearMonth.value;
  if (!ym || ym.length < 6) return '';
  return `${ym.substring(0, 4)}년 ${ym.substring(4, 6)}월`;
});

// 종사자구분별 색상
const getJobColor = (job: string) => {
  const colors: Record<string, string> = {
    '요양보호사': '#3b82f6',
    '사회복지사': '#8b5cf6',
    '센터장': '#f59e0b',
    '간호사': '#10b981',
    '간호조무사': '#06b6d4',
    '물리치료사': '#ec4899',
    '작업치료사': '#f43f5e',
  };
  return colors[job] || '#6b7280';
};

interface InsuranceDetail {
  nationalPension: number;
  healthInsurance: number;
  longTermCare: number;
  employmentInsurance: number;
  unemployment: number;
  industrialAccident: number;
}

interface JobSummaryRow {
  job: string;
  count: number;
  totalPay: number;
  socialInsurance: number;
  retirement: number;
  detail: InsuranceDetail;
}

// 간접비 종사자구분 (테이블에서 제외)
const indirectCostJobs = ['센터장'];

// 종사자구분별 요약 데이터
const jobSummaryData = computed<JobSummaryRow[]>(() => {
  if (!workInfoData.value || workInfoData.value.length === 0) return [];

  const jobMap = new Map<string, JobSummaryRow>();

  workInfoData.value.forEach((row: any) => {
    const job = row.emp_job || '기타';
    // 간접비 해당 종사자 제외
    if (indirectCostJobs.includes(job)) return;
    if (!jobMap.has(job)) {
      jobMap.set(job, {
        job,
        count: 0,
        totalPay: 0,
        socialInsurance: 0,
        retirement: 0,
        detail: {
          nationalPension: 0,
          healthInsurance: 0,
          longTermCare: 0,
          employmentInsurance: 0,
          unemployment: 0,
          industrialAccident: 0
        }
      });
    }

    const entry = jobMap.get(job)!;
    entry.count++;
    entry.totalPay += (row.ewi_total_pay || 0);

    // 사회보험 기관부담금 상세
    const centerYeon = row.ewi_center_yeon || 0;
    const centerGun = row.ewi_center_gun || 0;
    const centerJang = row.ewi_center_jang || 0;
    const centerGo = row.ewi_center_go || 0;
    const centerSil = row.ewi_center_sil || 0;
    const centerSan = row.ewi_center_san || 0;

    entry.detail.nationalPension += centerYeon;
    entry.detail.healthInsurance += centerGun;
    entry.detail.longTermCare += centerJang;
    entry.detail.employmentInsurance += centerGo;
    entry.detail.unemployment += centerSil;
    entry.detail.industrialAccident += centerSan;

    entry.socialInsurance += (centerYeon + centerGun + centerJang + centerGo + centerSil + centerSan);
    entry.retirement += (row.ewi_center_out || 0);
  });

  // 정렬: 요양보호사 > 사회복지사 > 센터장 > 기타
  const jobOrder = ['요양보호사', '사회복지사', '센터장', '간호사', '간호조무사', '물리치료사', '작업치료사'];
  return Array.from(jobMap.values()).sort((a, b) => {
    const aIdx = jobOrder.indexOf(a.job);
    const bIdx = jobOrder.indexOf(b.job);
    const aOrder = aIdx === -1 ? 999 : aIdx;
    const bOrder = bIdx === -1 ? 999 : bIdx;
    return aOrder - bOrder;
  });
});

// 합계
const jobSummaryTotals = computed(() => {
  const totals = {
    count: 0,
    totalPay: 0,
    socialInsurance: 0,
    retirement: 0,
    detail: {
      nationalPension: 0,
      healthInsurance: 0,
      longTermCare: 0,
      employmentInsurance: 0,
      unemployment: 0,
      industrialAccident: 0
    }
  };

  jobSummaryData.value.forEach(row => {
    totals.count += row.count;
    totals.totalPay += row.totalPay;
    totals.socialInsurance += row.socialInsurance;
    totals.retirement += row.retirement;
    totals.detail.nationalPension += row.detail.nationalPension;
    totals.detail.healthInsurance += row.detail.healthInsurance;
    totals.detail.longTermCare += row.detail.longTermCare;
    totals.detail.employmentInsurance += row.detail.employmentInsurance;
    totals.detail.unemployment += row.detail.unemployment;
    totals.detail.industrialAccident += row.detail.industrialAccident;
  });

  return totals;
});

// 인건비 지출비율 참고 모달 열기
const handleOpenJobSummary = async () => {
  if (isLoadingData.value) return;

  if (!hasDownloadableData.value) {
    await loadDownloadData();
  }

  if (!hasDownloadableData.value) {
    store.commit('showSnackbar', {
      text: '급여 데이터가 없습니다. 먼저 급여 데이터를 확인해주세요.',
      color: 'warning'
    });
    return;
  }

  showJobSummaryDialog.value = true;
};

// 원단위 절삭 함수 (예: 51131 -> 51130)
const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

// 만 나이 계산 함수
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;
  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth, 0);
  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const formatToYMD = (value?: string | null) => {
  if (!value) return '';
  const str = String(value).trim();
  if (!str) return '';
  if (str.includes('-')) return str;
  const digits = str.replace(/[^0-9]/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return str;
};

// 270분초과 수가 여부 확인
const is270MinutesOverSuga = (patient: any): boolean => {
  return patient.sugaMinutesKey != null || (patient.sugaName && patient.sugaName.includes('270분초과'));
};

// 환자별 수가시간 가져오기 (270분초과 수가는 30분 휴게시간 적용)
// 단, 8시간 이상 시에는 휴게시간 차감 없음
const getPatientSugaHoursForCalc = (patient: any): number => {
  const baseHours = patient.sugaHours || 0;
  
  if (is270MinutesOverSuga(patient)) {
    if (baseHours >= 8) {
      return baseHours;
    }
    return Math.max(0, baseHours - 0.5);
  }
  
  return baseHours;
};

// 수가 키 생성 (StepThree와 동일)
const getSugaKeyForCalc = (patient: any): string => {
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}`;
  }
  return patient.sugaCode;
};

// 요양보호사 급여 계산 함수 (StepThree 로직 동일)
const calculatePayrollForYobo = (group: any, groupIndex: number) => {
  const settings = settingsData.value[groupIndex];
  if (!settings) return null;

  // 총 수가시간, 서비스 횟수 계산 (270분초과 휴게시간 적용)
  let totalSugaHours = 0;
  let serviceDays = 0;
  group.patients?.forEach((patient: any) => {
    const sugaKey = getSugaKeyForCalc(patient);
    let patientSugaHours: number;
    
    // 수동 조정된 수가시간이 있으면 사용, 없으면 휴게시간 적용된 시간 사용
    if (settings.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
      patientSugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
    } else {
      patientSugaHours = getPatientSugaHoursForCalc(patient);
    }
    
    totalSugaHours += (patient.serviceDays || 0) * patientSugaHours;
    serviceDays += patient.serviceDays || 0;
  });

  // 평균 시급 계산 (가중평균) - 270분초과 휴게시간 적용
  let totalWeightedPay = 0;
  let totalHours = 0;
  
  group.patients?.forEach((patient: any) => {
    const sugaKey = getSugaKeyForCalc(patient);
    const timePay = settings.timePays?.[patient.patientCode]?.[sugaKey];
    if (timePay) {
      const numericPay = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
      
      // 수동 조정된 수가시간이 있으면 사용, 없으면 휴게시간 적용된 시간 사용
      let patientSugaHours: number;
      if (settings.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
        patientSugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
      } else {
        patientSugaHours = getPatientSugaHoursForCalc(patient);
      }
      const sugaHours = (patient.serviceDays || 0) * patientSugaHours;
      
      if (numericPay > 0 && sugaHours > 0) {
        totalWeightedPay += numericPay * sugaHours;
        totalHours += sugaHours;
      }
    }
  });

  const avgTimePay = totalHours > 0 ? Math.round(totalWeightedPay / totalHours) : 0;
  const isFamilyOnlyCaregiver = group.patients?.every((p: any) => (p.sugaName || '').includes('가족')) || false;
  const totalMonthlyPay = totalWeightedPay;
  const longServicePay = settings.longServicePay ? 
    parseFloat(String(settings.longServicePay).replace(/[^\d]/g, '')) || 0 : 0;
  const etcAllowance = settings.etcAllowance ? 
    parseFloat(String(settings.etcAllowance).replace(/[^\d]/g, '')) || 0 : 0;
  const bosuEduPay = settings.bosuEduPay ? 
    parseFloat(String(settings.bosuEduPay).replace(/[^\d]/g, '')) || 0 : 0;
  
  // 수당 계산 (60시간 이상일 때만)
  const canApplyAllowances = totalSugaHours >= 60;
  
  // 휴일수당: 공휴일가산 수가 서비스 시간 기반으로 계산
  // 수가명에 '공휴일가산', '공휴일 가산' 또는 '공휴일'과 '가산'이 포함된 서비스만 대상
  let holidayTotalHours = 0;
  group.patients?.forEach((patient: any) => {
    const sugaName = patient.sugaName || '';
    const nameLower = sugaName.toLowerCase().replace(/\s/g, '');
    const isHolidaySuga = nameLower.includes('공휴일가산') || 
      (nameLower.includes('공휴일') && nameLower.includes('가산')) ||
      nameLower.includes('일요일가산') ||
      (nameLower.includes('일요일') && nameLower.includes('가산'));
    if (isHolidaySuga) {
      holidayTotalHours += (patient.serviceDays || 0) * (patient.sugaHours || 0);
    }
  });
  
  // 휴일수당/연장수당은 최저시급 기준으로 계산 (StepThree와 동일)
  const minWageForAllowance = taxData.value?.tr_time_pay || 0;
  const holidayAllowance = settings.applyHoliday && canApplyAllowances && holidayTotalHours > 0 && minWageForAllowance > 0 ? 
    Math.round(minWageForAllowance * holidayTotalHours * 0.5) : 0;
  const overtimeAllowance = settings.applyOvertime && canApplyAllowances && minWageForAllowance > 0 ? 
    Math.min(Math.round(minWageForAllowance * 4 * 1.5), 200000) : 0;
  
  // 기본급 등 계산
  let basicPay = 0;
  let holidayPay = 0;
  let vacationPay = 0;
  let actualMealAllowance = 0;
  
  if (isFamilyOnlyCaregiver) {
    basicPay = totalMonthlyPay;
  } else {
    const minWage = taxData.value?.tr_time_pay || 0;
    if (minWage > 0) {
      basicPay = minWage * totalSugaHours;
      holidayPay = Math.round(basicPay * 0.2);
      vacationPay = serviceDays > 0 ? Math.round((minWage * totalSugaHours / serviceDays) * 11 / 12) : 0;
      
      // 식대 계산
      if (settings.applyMeal && group.totalLunchTimeCount > 0) {
        const requestedMealAllowance = Math.min(group.totalLunchTimeCount * 20000, 200000);
        const currentBasicTotal = basicPay + holidayPay + vacationPay;
        const remainingAmount = Math.max(0, totalMonthlyPay - currentBasicTotal);
        const availableAmount = Math.min(requestedMealAllowance, remainingAmount);
        const roundedAmount = Math.floor(availableAmount / 100) * 100;
        if (roundedAmount >= 5000) {
          actualMealAllowance = roundedAmount;
        }
      }
      
      const currentTotal = basicPay + holidayPay + vacationPay + actualMealAllowance;
      if (currentTotal < totalMonthlyPay) {
        vacationPay += (totalMonthlyPay - currentTotal);
      }
    } else {
      basicPay = totalMonthlyPay;
    }
  }
  
  const basicTotal = isFamilyOnlyCaregiver ? totalMonthlyPay : (basicPay + holidayPay + vacationPay + actualMealAllowance);
  const totalPay = basicTotal + longServicePay + etcAllowance + bosuEduPay + holidayAllowance + overtimeAllowance;
  
  // 비과세 처리
  const isSalaryUnder2_1M = basicTotal <= 2100000;
  
  // 휴일수당 + 연장수당 비과세 처리 (합계 20만원 한도)
  let nonTaxableHoliday = 0;
  let nonTaxableOvertime = 0;
  
  if (canApplyAllowances && isSalaryUnder2_1M) {
    const totalOvertimeHolidayAllowance = holidayAllowance + overtimeAllowance;
    
    if (totalOvertimeHolidayAllowance <= 200000) {
      nonTaxableHoliday = holidayAllowance;
      nonTaxableOvertime = overtimeAllowance;
    } else {
      const nonTaxableRatio = 200000 / totalOvertimeHolidayAllowance;
      nonTaxableHoliday = Math.round(holidayAllowance * nonTaxableRatio);
      nonTaxableOvertime = Math.round(overtimeAllowance * nonTaxableRatio);
      const totalNonTaxable = nonTaxableHoliday + nonTaxableOvertime;
      if (totalNonTaxable !== 200000) {
        nonTaxableOvertime += (200000 - totalNonTaxable);
      }
    }
  }
  
  // 식대 비과세 한도 (20만원)
  const nonTaxableMeal = Math.min(actualMealAllowance, 200000);
  const taxableMeal = actualMealAllowance - nonTaxableMeal;
  
  const taxableHoliday = holidayAllowance - nonTaxableHoliday;
  const taxableOvertime = overtimeAllowance - nonTaxableOvertime;
  const taxableAmount = basicTotal + longServicePay + etcAllowance + bosuEduPay + taxableHoliday + taxableOvertime + taxableMeal - actualMealAllowance;
  const nonTaxableAmount = nonTaxableMeal + nonTaxableHoliday + nonTaxableOvertime;

  // 보험 요율
  // 보험 요율 (taxData에서 동적으로 가져옴)
  const insuranceRates = {
    nationalPension: taxData.value?.tr_yeon || 0.045,
    healthInsurance: taxData.value?.tr_gun || 0.03545,
    longTermCare: taxData.value?.tr_jang || 0.1295,
    employmentInsurance: taxData.value?.tr_go || 0.009,
    unemployment: taxData.value?.tr_sil || 0.009,
    industrialAccident: props.sanRate || taxData.value?.tr_san || 0.0178
  };

  const age = calculateAge(group.caregiverInfo?.caregiver_birth || '', yearMonth.value);
  
  // 국민연금 금액 사용 (체크박스 활성화 + 입력값이 있으면 사용, 체크 해제 시 0) - StepThree와 동일
  const yeonPayAmount = settings.applyNationalPension && settings.yeonPay ? 
    parseFloat(String(settings.yeonPay).replace(/[^\d]/g, '')) || 0 : 0;
  const canApplyPension = settings.applyNationalPension && yeonPayAmount > 0;
  // 고용보험 적용 여부 (체크박스 상태가 제한사항보다 우선) - StepThree와 동일
  const canApplyEmployment = settings.applyEmploymentInsurance;
  // 실업급여 적용 여부 (65세 이상은 고용보험과 별도 관리) - StepThree와 동일
  const canApplyUnemployment = settings.applyUnemployment;

  // 근로자 공제 (원단위 절삭 적용)
  const employeeDeductions = {
    nationalPension: canApplyPension ? truncateToTen(yeonPayAmount) : 0, // 직접 입력한 금액도 원단위 절삭
    healthInsurance: settings.applyHealthInsurance ? truncateToTen(taxableAmount * insuranceRates.healthInsurance) : 0,
    longTermCare: 0,
    employmentInsurance: canApplyEmployment ? truncateToTen(taxableAmount * insuranceRates.employmentInsurance) : 0,
    incomeTax: truncateToTen(taxableAmount * 0.03),
    localTax: 0
  };
  
  employeeDeductions.longTermCare = truncateToTen(employeeDeductions.healthInsurance * insuranceRates.longTermCare);
  employeeDeductions.localTax = truncateToTen(employeeDeductions.incomeTax * 0.1);

  // 본인부담금공제 금액 가져오기 (10원 단위 절사)
  const boninPayAmount = settings.applyBoninPay && settings.boninPay ? 
    truncateToTen(parseFloat(String(settings.boninPay).replace(/[^\d]/g, '')) || 0) : 0;

  // 기타공제 금액 가져오기
  const empEtcAmount = settings.applyEmpEtc && settings.empEtc ? 
    parseFloat(String(settings.empEtc).replace(/[^\d]/g, '')) || 0 : 0;

  // 지원공제 계산 (장기근속수당+중증수급자가산을 기관공제 방식으로 계산) - StepThree와 동일
  // 기관공제 항목: 국민연금 + 건강보험 + 장기요양 + 고용보험 + 실업급여 + 산재보험 + 퇴직금
  const severeCareAddition = 0; // 중증수급자가산 (현재 미사용)
  const supportDeductionBase = longServicePay + severeCareAddition;
  let supportDeduction = 0;
  if (supportDeductionBase > 0) {
    // 국민연금 (기관공제 = 근로자 부담과 동일)
    if (canApplyPension) {
      supportDeduction += truncateToTen(supportDeductionBase * insuranceRates.nationalPension);
    }
    // 건강보험 (기관공제 = 근로자 부담과 동일)
    let healthInsuranceSupport = 0;
    if (settings.applyHealthInsurance) {
      healthInsuranceSupport = truncateToTen(supportDeductionBase * insuranceRates.healthInsurance);
      supportDeduction += healthInsuranceSupport;
    }
    // 장기요양보험 (기관공제 = 근로자 부담과 동일) - 건강보험의 장기요양율
    if (settings.applyHealthInsurance) {
      supportDeduction += truncateToTen(healthInsuranceSupport * insuranceRates.longTermCare);
    }
    // 고용보험 (기관공제 = 근로자 부담과 동일) - StepThree와 동일
    if (canApplyEmployment) {
      supportDeduction += truncateToTen(supportDeductionBase * insuranceRates.employmentInsurance);
    }
    // 실업급여 (사업주 부담) - 실업급여 체크박스 (65세 이상은 별도 선택 가능) - StepThree와 동일
    if (canApplyUnemployment) {
      supportDeduction += truncateToTen(supportDeductionBase * insuranceRates.unemployment);
    }
    // 산재보험 (기관공제 - 사업주만 부담) - 항상 적용
    supportDeduction += truncateToTen(supportDeductionBase * insuranceRates.industrialAccident);
    // 퇴직금 (기관공제) - 지원공제 기준금액 ÷ 12 - StepThree와 동일
    if (settings.applyRetirement) {
      supportDeduction += truncateToTen(supportDeductionBase / 12);
    }
  }

  const totalEmployeeDeduction = 
    employeeDeductions.nationalPension + employeeDeductions.healthInsurance +
    employeeDeductions.longTermCare + employeeDeductions.employmentInsurance +
    employeeDeductions.incomeTax + employeeDeductions.localTax +
    supportDeduction + boninPayAmount + empEtcAmount;

  // 기관 공제 (체크박스 상태가 제한사항보다 우선, 원단위 절삭 적용) - StepThree와 동일
  const centerDeductions = {
    nationalPension: employeeDeductions.nationalPension,
    healthInsurance: employeeDeductions.healthInsurance,
    longTermCare: employeeDeductions.longTermCare,
    employmentInsurance: employeeDeductions.employmentInsurance,
    unemployment: canApplyUnemployment ? truncateToTen(taxableAmount * insuranceRates.unemployment) : 0,
    industrialAccident: truncateToTen(taxableAmount * insuranceRates.industrialAccident),
    retirement: settings.applyRetirement ? truncateToTen(totalPay / 12) : 0
  };

  const totalCenterDeduction =
    centerDeductions.nationalPension + centerDeductions.healthInsurance +
    centerDeductions.longTermCare + centerDeductions.employmentInsurance +
    centerDeductions.unemployment + centerDeductions.industrialAccident +
    centerDeductions.retirement;

  const netPay = totalPay - totalEmployeeDeduction;

  // 비과세 절감액 (TabYoboWage.vue 로직과 동일하게 적용)
  let centerTaxSavings = 0;
  if (nonTaxableAmount > 0 && taxData.value) {
    // 1. 국민연금 절감 (사업주 부담분) - 60세 미만 & 적용 시에만
    const centerPensionSavings = canApplyPension ? Math.round(nonTaxableAmount * (taxData.value.tr_yeon || 0.045)) : 0;
    
    // 2. 건강보험 + 장기요양보험 절감 (사업주 부담분) - 항상 적용
    const centerHealthSavings = Math.round(nonTaxableAmount * (taxData.value.tr_gun || 0.03545));
    const centerLongTermCareSavings = Math.round(centerHealthSavings * (taxData.value.tr_jang || 0.1295));
    
    // 3. 고용보험 절감 (사업주 부담분) - 65세 미만 & 적용 시에만
    const centerEmploymentSavings = canApplyEmployment ? Math.round(nonTaxableAmount * (taxData.value.tr_go || 0.009)) : 0;
    
    // 4. 실업급여 절감 (사업주 부담분) - 나이와 상관없이 항상 적용
    const unemploymentSavings = Math.round(nonTaxableAmount * (taxData.value.tr_sil || 0.009));
    
    // 5. 산재보험 절감 (사업주 부담분) - 나이와 상관없이 항상 적용 (기관별 산재보험요율 우선)
    const industrialAccidentSavings = Math.round(nonTaxableAmount * (props.sanRate || taxData.value.tr_san || 0.0178));
    
    centerTaxSavings = Math.max(0, centerPensionSavings + centerHealthSavings + centerLongTermCareSavings + centerEmploymentSavings + unemploymentSavings + industrialAccidentSavings);
  }

  return {
    avgTimePay,
    basicPay, holidayPay, vacationPay,
    mealAllowance: actualMealAllowance,
    basicTotal, longServicePay, etcAllowance, bosuEduPay,
    severeCareAddition: 0,
    holidayAllowance, overtimeAllowance, totalPay,
    taxableAmount, nonTaxableAmount,
    employeeDeductions, centerDeductions,
    supportDeduction,      // 지원공제
    boninPayAmount,        // 본인부담금공제
    empEtcAmount,          // 기타공제
    totalEmployeeDeduction, totalCenterDeduction,
    netPay, centerTaxSavings, employeeTaxSavings: 0,
    suga_hours: totalSugaHours,
    service_days: serviceDays
  };
};

// 요양보호사 데이터 가져오기
const fetchYoboData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !yearMonth.value) return;

  try {
    // 세금 데이터 가져오기
    const year = yearMonth.value.substring(0, 4);
    const taxResponse = await axios.get(`/api/calc/tax/${year}`);
    if (taxResponse.data.success) {
      taxData.value = taxResponse.data.data.taxRate;
    }

    // 요양보호사 데이터 가져오기
    const memberJoinResponse = await axios.get('/api/calc/caregiver-member-join', {
      params: {
        sisulCode: user.m_sisul_code,
        lebYm: yearMonth.value
      },
      withCredentials: true
    });

    const rawMemberJoinData = memberJoinResponse.data.data || [];

    // 데이터 그룹화
    const grouped = new Map();
    
    rawMemberJoinData.forEach((item: any) => {
      const key = `${item.caregiver_name}_${item.caregiver_birth}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          name: item.caregiver_name,
          birth: item.caregiver_birth,
          caregiverInfo: {
            caregiver_name: item.caregiver_name,
            caregiver_birth: item.caregiver_birth,
            lem_gender: item.lem_gender,
            lem_license: item.lem_license,
            lem_work_start_date: item.lem_work_start_date,
            lem_work_end_date: item.lem_work_end_date
          },
          patients: [],
          originalItems: [],
          totalSugaMoney: 0,
          totalLunchTimeCount: 0
        });
      }
      
      const group = grouped.get(key);
      const sugaMoney = parseFloat(String(item.total_suga_money || 0).replace(/[^\d]/g, '')) || 0;
      group.totalSugaMoney += sugaMoney;
      const lunchCount = Number(item.lunch_time_count) || 0;
      group.totalLunchTimeCount += lunchCount;
      
      group.patients.push({
        patientCode: item.leb_patient_code,
        patientName: item.leb_patient_name || item.leb_patient_code,
        sugaCode: item.suga_code,
        sugaName: item.suga_name || '-',
        timePay: item.time_pay || '',
        serviceDays: item.service_days || 0,
        sugaHours: parseFloat(String(item.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0,
        totalSugaMoney: sugaMoney,
        lunchTimeCount: lunchCount
      });
      
      group.originalItems.push(item);
    });

    // 그룹화된 데이터를 배열로 변환
    groupedMemberData.value = Array.from(grouped.values()).map((group, index) => {
      // 설정 데이터 초기화
      if (!settingsData.value[index]) {
        const firstItem = group.originalItems[0];
        const timePays: Record<string, Record<string, string>> = {};
        group.patients.forEach((patient: any) => {
          if (!timePays[patient.patientCode]) {
            timePays[patient.patientCode] = {};
          }
          let timePay = patient.timePay || '';
          if (timePay) {
            const numericValue = parseFloat(String(timePay).replace(/[^\d]/g, '')) || 0;
            if (numericValue > 0) {
              timePay = new Intl.NumberFormat('ko-KR').format(numericValue);
            }
          }
          timePays[patient.patientCode][patient.sugaCode] = timePay;
        });

        // 국민연금 금액 포맷팅
        let yeonPay = firstItem?.lemi_yeon_pay || '';
        let applyNationalPension = false;
        if (yeonPay) {
          const numericValue = parseFloat(String(yeonPay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyNationalPension = true;
          }
        }

        // 장기근속수당 포맷팅
        let longServicePay = firstItem?.lemi_long_service_pay || '';
        let applyLongServicePay = false;
        if (longServicePay) {
          const numericValue = parseFloat(String(longServicePay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            longServicePay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyLongServicePay = true;
          }
        }

        // 보수교육비 포맷팅
        let bosuEduPay = firstItem?.lemi_bosu_edu_pay || '';
        let applyBosuEduPay = false;
        if (bosuEduPay) {
          const numericValue = parseFloat(String(bosuEduPay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            bosuEduPay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyBosuEduPay = true;
          }
        }

        // 본인부담금공제 포맷팅
        let boninPay = firstItem?.lemi_bonin_pay || '';
        let applyBoninPay = false;
        if (boninPay) {
          const numericValue = parseFloat(String(boninPay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            boninPay = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyBoninPay = true;
          }
        }

        // 기타공제 포맷팅
        let empEtc = firstItem?.lemi_emp_etc || '';
        let applyEmpEtc = false;
        if (empEtc) {
          const numericValue = parseFloat(String(empEtc).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            empEtc = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyEmpEtc = true;
          }
        }

        // 기타수당 포맷팅
        let etcAllowance = firstItem?.lemi_etc_allowance || '';
        let applyEtcAllowance = false;
        if (etcAllowance) {
          const numericValue = parseFloat(String(etcAllowance).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            etcAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
            applyEtcAllowance = true;
          }
        }

        settingsData.value[index] = {
          applyNationalPension: applyNationalPension,
          yeonPay: yeonPay,
          applyHealthInsurance: firstItem?.lemi_apply_gun === 'Y',
          applyEmploymentInsurance: firstItem?.lemi_apply_go === 'Y',
          applyRetirement: firstItem?.lemi_apply_out_pay === 'Y',
          applyHoliday: firstItem?.lemi_apply_holiday_pay === 'Y',
          applyOvertime: firstItem?.lemi_apply_overtime_pay === 'Y',
          applyMeal: firstItem?.lemi_apply_lunch_pay === 'Y',
          applyLongServicePay: applyLongServicePay,
          longServicePay: longServicePay,
          applyEtcAllowance: applyEtcAllowance,
          etcAllowance: etcAllowance,
          applyBosuEduPay: applyBosuEduPay,
          bosuEduPay: bosuEduPay,
          applyBoninPay: applyBoninPay,
          boninPay: boninPay,
          applyEmpEtc: applyEmpEtc,
          empEtc: empEtc,
          timePays: timePays
        };
      }
      
      // 급여 상세 계산
      const payroll = calculatePayrollForYobo(group, index);
      
      // 가족요양 전용 여부 확인
      const isFamilyOnlyCaregiver = group.patients?.every((p: any) => 
        (p.sugaName || '').includes('가족')
      ) || false;
      
      // payrollDetails 저장 (WageSlipPdf에서 사용)
      payrollDetails.value[index] = {
        avgTimePay: payroll?.avgTimePay || 0,
        basicPay: payroll?.basicPay || 0,
        holidayPay: payroll?.holidayPay || 0,
        vacationPay: payroll?.vacationPay || 0,
        mealAllowance: payroll?.mealAllowance || 0,
        longServicePay: payroll?.longServicePay || 0,
        etcAllowance: payroll?.etcAllowance || 0,
        bosuEduPay: payroll?.bosuEduPay || 0,
        severeCareAddition: payroll?.severeCareAddition || 0,
        holidayAllowance: payroll?.holidayAllowance || 0,
        overtimeAllowance: payroll?.overtimeAllowance || 0,
        totalPay: payroll?.totalPay || 0,
        employeeDeductions: payroll?.employeeDeductions || {},
        totalEmployeeDeduction: payroll?.totalEmployeeDeduction || 0,
        netPay: payroll?.netPay || 0,
        taxableAmount: payroll?.taxableAmount || 0,
        nonTaxableAmount: payroll?.nonTaxableAmount || 0,
        isFamilyOnlyCaregiver
      };
      
      // workTimeInfo 저장 (WageSlipPdf에서 사용)
      workTimeInfo.value[index] = {
        totalSugaHours: payroll?.suga_hours || 0,
        holidayMinutes: 0,    // 휴일근로 분 (나중에 실제 데이터로 대체)
        overtimeMinutes: 0,   // 연장근로 분 (나중에 실제 데이터로 대체)
        nightHours: 0
      };
      
      // WageReportExcel이 기대하는 구조로 변환
      return {
        ...group,
        _originalIndex: index,
        workStartDate: group.caregiverInfo?.lem_work_start_date || '',
        totals: {
          suga_hours: payroll?.suga_hours || 0,
          service_days: payroll?.service_days || 0,
          total_suga_money: group.totalSugaMoney,
          totalPay: payroll?.totalPay || 0,
          netPay: payroll?.netPay || 0,
          taxableAmount: payroll?.taxableAmount || 0,
          nonTaxableAmount: payroll?.nonTaxableAmount || 0,
          employeeTaxSavings: payroll?.employeeTaxSavings || 0,
          centerTaxSavings: payroll?.centerTaxSavings || 0,
          boninPayAmount: payroll?.boninPayAmount || 0,  // 본인부담금공제
          empEtcAmount: payroll?.empEtcAmount || 0       // 기타공제
        },
        allowances: {
          overtime: payroll?.overtimeAllowance || 0,
          holiday: payroll?.holidayAllowance || 0,
          laborDay: 0,
          longService: payroll?.longServicePay || 0,
          etcAllowance: payroll?.etcAllowance || 0,
          bosuEduPay: payroll?.bosuEduPay || 0,
          severeCareAddition: 0,
          meal: payroll?.mealAllowance || 0,
          other: 0
        },
        insurance: {
          nationalPension: { amount: payroll?.employeeDeductions?.nationalPension || 0 },
          healthInsurance: { amount: payroll?.employeeDeductions?.healthInsurance || 0 },
          longTermCare: { amount: payroll?.employeeDeductions?.longTermCare || 0 },
          employmentInsurance: { amount: payroll?.employeeDeductions?.employmentInsurance || 0 },
          unemployment: { amount: payroll?.centerDeductions?.unemployment || 0 },
          industrialAccident: { amount: payroll?.centerDeductions?.industrialAccident || 0 },
          supportDeduction: payroll?.supportDeduction || 0,
          boninPayDeduction: payroll?.boninPayAmount || 0,  // 본인부담금공제
          empEtcDeduction: payroll?.empEtcAmount || 0       // 기타공제
        },
        tax: {
          incomeTax: payroll?.employeeDeductions?.incomeTax || 0,
          localTax: payroll?.employeeDeductions?.localTax || 0
        }
      };
    });

  } catch (error) {
    console.error('요양보호사 데이터 조회 오류:', error);
    store.commit('showSnackbar', {
      text: '데이터를 불러오는 중 오류가 발생했습니다.',
      color: 'error'
    });
  }
};

// 사회복지사/센터장 급여 계산 함수 (StepFour의 TabSabokWage 로직 적용)
const calculatePayrollForSabok = (staff: any, settings: any) => {
  if (!settings) return null;

  const timePay = typeof settings.timePay === 'number' ? settings.timePay : parseInt(String(settings.timePay).replace(/,/g, '')) || 0;
  const workHours = typeof settings.workHours === 'number' ? settings.workHours : parseFloat(String(settings.workHours)) || 0;
  const totalPay = Math.floor(timePay * workHours);
  
  // 수당 계산
  const overtimeAllowance = typeof settings.overtimeAllowance === 'number' ? settings.overtimeAllowance : parseInt(String(settings.overtimeAllowance).replace(/,/g, '')) || 0;
  const nightAllowance = typeof settings.nightAllowance === 'number' ? settings.nightAllowance : parseInt(String(settings.nightAllowance).replace(/,/g, '')) || 0;
  const holidayAllowance = typeof settings.holidayAllowance === 'number' ? settings.holidayAllowance : parseInt(String(settings.holidayAllowance).replace(/,/g, '')) || 0;
  const annualLeaveAllowance = typeof settings.annualLeaveAllowance === 'number' ? settings.annualLeaveAllowance : parseInt(String(settings.annualLeaveAllowance).replace(/,/g, '')) || 0;
  const longServiceAllowance = typeof settings.longServiceAllowance === 'number' ? settings.longServiceAllowance : parseInt(String(settings.longServiceAllowance).replace(/,/g, '')) || 0;
  const mealAllowance = typeof settings.mealAllowance === 'number' ? settings.mealAllowance : parseInt(String(settings.mealAllowance).replace(/,/g, '')) || 0;
  const transportationFee = typeof settings.transportationFee === 'number' ? settings.transportationFee : parseInt(String(settings.transportationFee).replace(/,/g, '')) || 0;
  const educationFee = typeof settings.educationFee === 'number' ? settings.educationFee : parseInt(String(settings.educationFee).replace(/,/g, '')) || 0;
  const bonus = typeof settings.bonus === 'number' ? settings.bonus : parseInt(String(settings.bonus).replace(/,/g, '')) || 0;
  const vehicleAllowance = typeof settings.vehicleAllowance === 'number' ? settings.vehicleAllowance : parseInt(String(settings.vehicleAllowance).replace(/,/g, '')) || 0;
  const childEducationFee = typeof settings.childEducationFee === 'number' ? settings.childEducationFee : parseInt(String(settings.childEducationFee).replace(/,/g, '')) || 0;
  const otherAllowance = typeof settings.otherAllowance === 'number' ? settings.otherAllowance : parseInt(String(settings.otherAllowance).replace(/,/g, '')) || 0;

  const totalAllowances = overtimeAllowance + nightAllowance + holidayAllowance + annualLeaveAllowance + 
                          longServiceAllowance + mealAllowance + transportationFee + educationFee + 
                          bonus + vehicleAllowance + childEducationFee + otherAllowance;
  
  const finalTotalPay = totalPay + totalAllowances;

  // 과세/비과세 구분 (식대, 교통비, 교육비, 차량보조비, 자녀교육비는 비과세)
  const nonTaxableAmount = mealAllowance + transportationFee + educationFee + vehicleAllowance + childEducationFee;
  const taxableAmount = finalTotalPay - nonTaxableAmount;

  // 보험 요율 (taxData에서 동적으로 가져옴)
  const insuranceRates = {
    nationalPension: taxData.value?.tr_yeon || 0.045,
    healthInsurance: taxData.value?.tr_gun || 0.03545,
    longTermCare: taxData.value?.tr_jang || 0.1295,
    employmentInsurance: taxData.value?.tr_go || 0.009,
    unemployment: taxData.value?.tr_sil || 0.009,
    industrialAccident: props.sanRate || taxData.value?.tr_san || 0.0178
  };

  const age = calculateAge(settings.birthDate || staff.birth || '', yearMonth.value);
  const canApplyPension = age < 60;
  const canApplyEmployment = age < 65;

  // 근로자 공제 (원단위 절삭 적용)
  const employeeDeductions = {
    nationalPension: canApplyPension ? truncateToTen(taxableAmount * insuranceRates.nationalPension) : 0,
    healthInsurance: truncateToTen(taxableAmount * insuranceRates.healthInsurance),
    longTermCare: 0,
    employmentInsurance: canApplyEmployment ? truncateToTen(taxableAmount * insuranceRates.employmentInsurance) : 0,
    incomeTax: truncateToTen(taxableAmount * 0.03),
    localTax: 0
  };
  
  employeeDeductions.longTermCare = truncateToTen(employeeDeductions.healthInsurance * insuranceRates.longTermCare);
  employeeDeductions.localTax = truncateToTen(employeeDeductions.incomeTax * 0.1);

  const totalEmployeeDeduction = 
    employeeDeductions.nationalPension + employeeDeductions.healthInsurance +
    employeeDeductions.longTermCare + employeeDeductions.employmentInsurance +
    employeeDeductions.incomeTax + employeeDeductions.localTax;

  // 기관 공제 (원단위 절삭 적용)
  const centerDeductions = {
    nationalPension: employeeDeductions.nationalPension,
    healthInsurance: employeeDeductions.healthInsurance,
    longTermCare: employeeDeductions.longTermCare,
    employmentInsurance: employeeDeductions.employmentInsurance,
    unemployment: canApplyEmployment ? truncateToTen(taxableAmount * insuranceRates.unemployment) : 0,
    industrialAccident: truncateToTen(taxableAmount * insuranceRates.industrialAccident),
    retirement: truncateToTen(finalTotalPay / 12)
  };

  const totalCenterDeduction =
    centerDeductions.nationalPension + centerDeductions.healthInsurance +
    centerDeductions.longTermCare + centerDeductions.employmentInsurance +
    centerDeductions.unemployment + centerDeductions.industrialAccident +
    centerDeductions.retirement;

  const netPay = finalTotalPay - totalEmployeeDeduction;

  return {
    timePay, workHours, totalPay: finalTotalPay,
    overtimeAllowance, nightAllowance, holidayAllowance,
    annualLeaveAllowance, longServiceAllowance,
    mealAllowance, transportationFee, educationFee,
    bonus, vehicleAllowance, childEducationFee, otherAllowance,
    taxableAmount, nonTaxableAmount,
    employeeDeductions, centerDeductions,
    totalEmployeeDeduction, totalCenterDeduction,
    netPay, centerTaxSavings: 0, employeeTaxSavings: 0,
    age
  };
};

// 사회복지사/센터장 데이터 가져오기
const fetchSabokData = async () => {
  const user = store.state.user;
  if (!user || !user.m_sisul_code || !yearMonth.value) return;

  try {
    const response = await axios.get(`/api/calc/employee-sabok/${user.m_sisul_code}`);
    
    if (response.data.success) {
      const employees = response.data.data;
      const currentYm = yearMonth.value;
      
      // 입사일에 따라 필터링
      const filteredEmployees = employees.filter((emp: any) => {
        if (!emp.emp_in_date) return true;
        const empInDate = emp.emp_in_date.replace(/-/g, '');
        const empInYm = empInDate.substring(0, 6);
        return currentYm >= empInYm;
      });
      
      // 근무/급여 정보 조회
      const workInfoResponse = await axios.get(`/api/calc/work-info/${user.m_sisul_code}/${currentYm}`);
      const workInfoMap = new Map();
      
      if (workInfoResponse.data.success && workInfoResponse.data.data) {
        workInfoResponse.data.data.forEach((workInfo: any) => {
          workInfoMap.set(workInfo.emp_code, workInfo);
        });
      }
      
      // 데이터 변환
      sabokStaffData.value = filteredEmployees.map((emp: any) => {
        const workInfo = workInfoMap.get(emp.emp_code);
        let timePay = 0;
        if (workInfo?.ewi_basic_pay && workInfo?.ewi_suga_hours) {
          timePay = Math.round(workInfo.ewi_basic_pay / workInfo.ewi_suga_hours);
        }
        
        const formattedBirth = formatToYMD(emp.emp_birth);
        
        return {
          empCode: emp.emp_code,
          name: emp.emp_name || '',
          type: emp.emp_job || '',
          timePay: timePay,
          workHours: workInfo?.ewi_suga_hours || 0,
          salary: workInfo?.ewi_basic_pay || 0,
          allowance: (workInfo?.ewi_overtime_pay || 0) + (workInfo?.ewi_holiday_pay || 0) + 
                     (workInfo?.ewi_annual_pay || 0) + (workInfo?.ewi_long_service_pay || 0) +
                     (workInfo?.ewi_bosu_edu_pay || 0) + (workInfo?.ewi_meal_pay || 0) + (workInfo?.ewi_other_pay || 0),
          totalSalary: workInfo?.ewi_total_pay || 0,
          workStartDate: emp.emp_in_date || '',
          workEndDate: emp.emp_out_date || '',
          birth: formattedBirth,
          phone: emp.emp_phone || '',
          email: emp.emp_email || '',
          address: emp.emp_addr || '',
          addressDetail: emp.emp_addr_detail || '',
          isBoss: emp.emp_isboss === 'Y'
        };
      });

      // 설정 데이터 초기화 및 급여 계산
      sabokStaffData.value.forEach((staff, index) => {
        if (!sabokStaffSettings.value[index]) {
          const workInfo = workInfoMap.get(staff.empCode);
          const isBoss = staff.isBoss || false;
          
          sabokStaffSettings.value[index] = {
            name: staff.name,
            type: staff.type,
            birthDate: formatToYMD(staff.birth),
            workStartDate: staff.workStartDate,
            timePay: staff.timePay,
            workHours: staff.workHours,
            overtimeHours: 0,
            nightHours: 0,
            holidayHours: 0,
            overtimeAllowance: workInfo?.ewi_overtime_pay || 0,
            nightAllowance: 0,
            holidayAllowance: workInfo?.ewi_holiday_pay || 0,
            annualLeaveAllowance: workInfo?.ewi_annual_pay || 0,
            longServiceAllowance: workInfo?.ewi_long_service_pay || 0,
            bosuEduPayAllowance: workInfo?.ewi_bosu_edu_pay || 0,
            transportationFee: 0,
            educationFee: 0,
            bonus: 0,
            otherAllowance: workInfo?.ewi_other_pay || 0,
            mealAllowance: workInfo?.ewi_meal_pay || 0,
            vehicleAllowance: 0,
            childEducationFee: 0,
            isBoss: isBoss
          };
          
          // 급여 계산 결과 추가
          const payroll = calculatePayrollForSabok(staff, sabokStaffSettings.value[index]);
          if (payroll) {
            // DB에 저장된 공제 금액이 있으면 사용, 없으면 계산 값 사용
            // 대표자인 경우 고용보험, 실업급여, 산재보험, 퇴직금은 0원
            sabokStaffSettings.value[index].insurance = {
              nationalPension: { amount: workInfo?.ewi_emp_guk ?? payroll.employeeDeductions.nationalPension },
              healthInsurance: { amount: workInfo?.ewi_emp_gun ?? payroll.employeeDeductions.healthInsurance },
              longTermCare: { amount: workInfo?.ewi_emp_jang ?? payroll.employeeDeductions.longTermCare },
              employmentInsurance: { amount: isBoss ? 0 : (workInfo?.ewi_emp_goy ?? payroll.employeeDeductions.employmentInsurance) },
              unemployment: { amount: isBoss ? 0 : (workInfo?.ewi_center_sil ?? payroll.centerDeductions.unemployment) },
              industrialAccident: { amount: isBoss ? 0 : (workInfo?.ewi_center_san ?? payroll.centerDeductions.industrialAccident) },
              retirement: { amount: isBoss ? 0 : (workInfo?.ewi_center_out ?? payroll.centerDeductions.retirement) },
              supportDeduction: { amount: workInfo?.ewi_emp_support_deduction || 0 }
            };
            sabokStaffSettings.value[index].tax = {
              incomeTax: workInfo?.ewi_emp_income_tax ?? payroll.employeeDeductions.incomeTax,
              localTax: workInfo?.ewi_emp_local_tax ?? payroll.employeeDeductions.localTax
            };
            sabokStaffSettings.value[index].totals = {
              netPay: workInfo?.ewi_net_pay ?? payroll.netPay,
              nonTaxableAmount: payroll.nonTaxableAmount,
              employeeTaxSavings: payroll.employeeTaxSavings,
              centerTaxSavings: payroll.centerTaxSavings
            };
            sabokStaffSettings.value[index].age = payroll.age;
          }
        }
      });
    }
  } catch (error) {
    console.error('사회복지사/센터장 데이터 조회 오류:', error);
  }
};

// 엑셀 다운로드
const handleDownloadExcel = async () => {
  if (!wageReportExcelRef.value) return;

  if (!hasDownloadableData.value) {
    await loadDownloadData();
  }

  if (!hasDownloadableData.value) {
    store.commit('showSnackbar', {
      text: '다운로드할 데이터가 없습니다. 먼저 급여 데이터를 확인해주세요.',
      color: 'warning'
    });
    return;
  }
  
  try {
    if (isLoadingData.value) {
      await loadDownloadData();
    }
    await wageReportExcelRef.value.downloadPayrollReport();
  } catch (error) {
    console.error('엑셀 다운로드 오류:', error);
    store.commit('showSnackbar', {
      text: '엑셀 다운로드 중 오류가 발생했습니다.',
      color: 'error'
    });
  }
};

// Snackbar 핸들러
const handleSnackbar = (message: string, type: string) => {
  store.commit('showSnackbar', {
    text: message,
    color: type
  });
};

// 확정 버튼 클릭
const handleConfirm = () => {
  showConfirmDialog.value = true;
};

// 확정 진행
const proceedConfirm = () => {
  showConfirmDialog.value = false;
  emit('confirm');
};

// 해제 버튼 클릭
const handleRelease = () => {
  // 임금신고 요청을 하지 않았거나 취소한 경우에만 해제 가능
  if (!canReleaseConfirmation.value) {
    const status = taxRequestData.value?.wtr_status;
    let message = '임금신고 요청 중에는 확정을 해제할 수 없습니다.';
    
    if (status === '요청') {
      message = '임금신고 요청 중에는 확정을 해제할 수 없습니다. 먼저 요청을 취소해주세요.';
    } else if (status === '승인') {
      message = '임금신고가 승인된 상태에서는 확정을 해제할 수 없습니다.';
    } else if (status === '미승인') {
      message = '임금신고가 미승인된 상태입니다. 새로운 요청을 진행해주세요.';
    }
    
    store.commit('showSnackbar', {
      text: message,
      color: 'warning'
    });
    return;
  }
  showReleaseDialog.value = true;
};

// 해제 진행
const proceedRelease = () => {
  showReleaseDialog.value = false;
  emit('release');
};

let dataLoadPromise: Promise<void> | null = null;

const loadDownloadData = async () => {
  // 이미 로딩 중이면 해당 프로미스를 기다림
  if (isLoadingData.value && dataLoadPromise) {
    await dataLoadPromise;
    return;
  }
  
  if (!stepCompleted.value || !yearMonth.value) return;

  isLoadingData.value = true;
  dataLoadPromise = (async () => {
    try {
      // hh_emp_sabok_work_info 테이블에서 직접 데이터 가져오기
      const user = store.state.user;
      if (!user || !user.m_sisul_code) {
        workInfoData.value = [];
        return;
      }
      const response = await axios.get(`/api/calc/work-info/${user.m_sisul_code}/${yearMonth.value}`);
      if (response.data.success && response.data.data) {
        workInfoData.value = response.data.data;
      } else {
        workInfoData.value = [];
      }
      
      // PDF용 데이터는 기존 방식으로 유지
      await Promise.all([fetchYoboData(), fetchSabokData()]);
    } catch (error) {
      console.error('데이터 로드 중 오류:', error);
      store.commit('showSnackbar', {
        text: '데이터를 불러오는 중 오류가 발생했습니다.',
        color: 'error'
      });
    } finally {
      isLoadingData.value = false;
      dataLoadPromise = null;
    }
  })();
  await dataLoadPromise;
};

// stepCompleted 변경 시 데이터 로드 (immediate: true가 초기 로드 처리)
watch(() => props.stepCompleted, async (newVal) => {
  if (newVal) {
    await loadDownloadData();
  }
  // 임금신고 요청 상태 로드
  await loadTaxRequestStatus();
}, { immediate: true });

// yearMonth 변경 시 데이터 다시 로드
watch(() => props.yearMonth, async () => {
  if (stepCompleted.value) {
    await loadDownloadData();
  }
  // 임금신고 요청 상태 다시 로드
  await loadTaxRequestStatus();
});
</script>

<style lang="scss">
@import '@/assets/styles/wageProcess.scss';

.confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .confirm-dialog-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 32px 24px 24px 24px;
    border-bottom: 1px solid #f1f3f5;
    background: #ecfdf5;

    &.warning {
      background: #fef3c7;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 700;
      color: #191f28;
      margin: 0;
    }
  }

  .confirm-dialog-body {
    padding: 24px 32px;
    text-align: center;

    p {
      font-size: 0.938rem;
      color: #4b5563;
      line-height: 1.6;
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
        font-weight: 600;
        color: #191f28;
      }
    }
  }

  .confirm-dialog-footer {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;

    .btn-cancel {
      flex: 1;
      height: 48px;
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    }

    .btn-confirm {
      flex: 1;
      height: 48px;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);

      &:hover {
        background: #059669;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      &.warning {
        background: #f59e0b;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);

        &:hover {
          background: #d97706;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
        }
      }
    }
  }
}

.status-card-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.features-grid {
  .feature-item {
    &.clickable {
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      
      &:hover:not(.disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &.has-request {
        border-color: #10b981;
        background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
      }

      .request-badge {
        position: absolute;
        top: 8px;
        right: 8px;
        padding: 2px 8px;
        background: #10b981;
        color: white;
        font-size: 0.688rem;
        font-weight: 700;
        border-radius: 10px;
      }
    }
  }
}

// 인건비 지출비율 참고 모달 스타일
.job-summary-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .job-summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-bottom: 1px solid #bfdbfe;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;

      h3 {
        font-size: 1.15rem;
        font-weight: 700;
        color: #1e3a8a;
        margin: 0;
        line-height: 1.3;
      }

      .header-subtitle {
        font-size: 0.813rem;
        color: #6b7280;
        margin: 2px 0 0 0;
      }
    }
  }

  .job-summary-body {
    padding: 20px 24px;

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 40px 0;
      color: #9ca3af;

      p {
        font-size: 0.875rem;
        margin: 0;
      }
    }

    .table-wrapper {
      overflow-x: auto;
    }
  }

  .job-summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;

    thead th {
      background: #f8fafc;
      color: #374151;
      font-weight: 600;
      padding: 10px 12px;
      border-bottom: 2px solid #e5e7eb;
      text-align: center;
      white-space: nowrap;

      .sub-label {
        font-size: 0.688rem;
        font-weight: 400;
        color: #9ca3af;
      }
    }

    tbody td {
      padding: 10px 12px;
      border-bottom: 1px solid #f1f3f5;
      text-align: right;
      color: #374151;
    }

    tfoot td {
      padding: 12px 12px;
      border-top: 2px solid #1e3a8a;
      text-align: right;
      color: #1e3a8a;
      background: #f8fafc;
    }

    .col-job {
      text-align: left !important;
      white-space: nowrap;
    }

    .col-num {
      text-align: center !important;
      white-space: nowrap;
    }

    .col-money {
      text-align: right !important;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }

    .col-money-sm {
      text-align: right !important;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
      font-size: 0.813rem;
      padding: 8px 8px;
    }

    .total-highlight {
      color: #1e3a8a;
      font-weight: 600;
    }

    .total-row td {
      font-weight: 700;
    }

    .job-badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      color: white;
    }

    &.detail-table {
      margin-top: 8px;
    }
  }

  .insurance-detail-section {
    margin-top: 20px;
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;

    .detail-title {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.875rem;
      font-weight: 600;
      color: #4b5563;
      margin: 0 0 8px 0;
      user-select: none;

      &:hover {
        color: #1e3a8a;
      }
    }

    .detail-table-wrapper {
      overflow-x: auto;
    }
  }

  .job-summary-footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;
  }
}
</style>